import {ChangeDetectionStrategy, Component, input} from "@angular/core";
import {AvstemningMedResultatModell, AvstemningsstatusModell, BeslutningModell, GruppeId, HtmlTekst, OpprettBeslutningSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {TranslateModule} from "@ngx-translate/core";
import {beslutningBeskrivelseEllerForslag, nullsikkerBeslutningTittelEllerForslag} from "felles-ng2/modell/beslutningModellHjelpere";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {erTomHtmlTekst} from "felles-ng2/støttefunksjoner/htmlTekst";
import {leggTilFeilDersom} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {beslutningBeskrivelseMedAvstemningSomMal} from "felles-ng2/modell/avstemningModellHjelpere";

const debug = initDebugger('BeslutningSkjemaKomponent');


export type BeslutningSkjemaType = ReturnType<typeof BeslutningSkjemaKomponent.opprettSkjema>;
export type BeslutningSkjemaVerdier = BeslutningSkjemaType['value'];
type DelSpesifikasjon = Pick<OpprettBeslutningSpesifikasjon, 'id' | 'tittel' | 'beskrivelse'>;

/**
 * Skjema for å opprette / redigere beslutning
 */
@Component({
    selector: 'beslutning-skjema',
    templateUrl: 'beslutning-skjema.html',
    styleUrl: 'beslutning-skjema.scss',
    host: {'data-testid': 'beslutning-skjema'},
    imports: [
        TranslateModule,
        FormsModule,
        MatError,
        MatFormField,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        StandardValideringsfeilPipe,
        TranslateModule,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BeslutningSkjemaKomponent {

    /** Oppretter skjema uten påkrevde felter.
     * Kaller er ansvarlig for å legge til valideringsregler vhja kall til {@link BeslutningSkjemaKomponent#settFelterPåkrevdForNyBeslutning}
     * eller {@link BeslutningSkjemaKomponent#settFelterPåkrevdForEksisterendeBeslutning}*/
    static opprettSkjema() {
        return new FormGroup({
            id: new FormControl(nyUuid(), {nonNullable: true}), //ikke eksponert for redigering
            tittel: new FormControl(''), //validering settes av settFelterPåkrevd()
            beskrivelse: new FormControl<HtmlTekst | null>(null, {validators: FeltValidatorer.htmlTekst})
        });
    }

    /** Populerer angitt skjema med data fra angitt beslutning */
    static populerSkjema(skjema: BeslutningSkjemaType, beslutning: BeslutningModell): BeslutningSkjemaType {
        skjema.patchValue({
            id: beslutning.id,
            tittel: nullsikkerBeslutningTittelEllerForslag(beslutning),
            beskrivelse: beslutningBeskrivelseEllerForslag(beslutning)
        });
        return skjema;
    }

    /** Populerer angitt skjema med beslutning basert på angitt avstemning */
    static populerSkjemaFraAvstemning(skjema: BeslutningSkjemaType, avstemning: AvstemningMedResultatModell, avstemningsstatus: AvstemningsstatusModell, oversettelser: Oversettelser): BeslutningSkjemaType {
        skjema.patchValue({tittel: avstemning.tittel, beskrivelse: beslutningBeskrivelseMedAvstemningSomMal(avstemning, avstemningsstatus, oversettelser)});
        return skjema;
    }

    /** Legger til valideringsregler som gjelder for skjema for eksisterende beslutning.
     * Krever at enten tittel eller beskrivelse er angitt */
    static settFelterPåkrevdForEksisterendeBeslutning(skjema: BeslutningSkjemaType): BeslutningSkjemaType {
        skjema.addValidators(skjema => {
            const beslutningSkjema = skjema as BeslutningSkjemaType;
            const bådeTittelOgBeskrivelseTomme = erTomEllerNil(beslutningSkjema.value.tittel) && erTomHtmlTekst(beslutningSkjema.value.beskrivelse);
            leggTilFeilDersom(bådeTittelOgBeskrivelseTomme, 'tittelOgBeskrivelseTom', beslutningSkjema.controls.tittel);
            //nødvendig for at valideringsfeil skal vises når bruker ikke allerede har vært innom tittel-feltet
            if (bådeTittelOgBeskrivelseTomme) beslutningSkjema.controls.tittel.markAsTouched();
            return null;
        });
        return skjema;
    }

    /** Legger til eller fjerner valideringsregler som gjelder for skjema for ny beslutning.
     * Krever at tittel er angitt */
    static settFelterPåkrevdForNyBeslutning(skjema: BeslutningSkjemaType, påkrevd = true): BeslutningSkjemaType {
        if (påkrevd)
            skjema.controls.tittel.addValidators(FeltValidatorer.storIkkeTomStreng);
        else
            skjema.controls.tittel.removeValidators(FeltValidatorer.storIkkeTomStreng);
        return skjema;
    }

    static somSpesifikasjon(skjema: BeslutningSkjemaType): DelSpesifikasjon {
        const verdier = skjema.value;
        return {
            id: verdier.id!,
            tittel: verdier.tittel!,
            beskrivelse: verdier.beskrivelse!,
        }
    }

    skjema = input.required<BeslutningSkjemaType>();
    gruppeId = input.required<GruppeId | null>();
    tittelLabel = input(this.oversettelser.oversett(i18n('Beslutning')));

    constructor(private oversettelser: Oversettelser) {
    }

    // private hjelpemetoder
}
